import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ReactComponent as KeyboardArrowDownIcon } from "../../asserts/logo/KeyboardArrowDown.svg";
import { INTAKE_COLORS, INTAKE_FONT } from "../../theme/intakeTokens";
import {
  VECTOR_STORE_PROVIDER_OPTIONS,
  buildVectorStoreConfigPayload,
  fieldsForProvider,
  sanitizePortInput,
  vectorStoreFormCanSave,
  vectorStoreFormCanTest,
  vectorReportMatchesForm,
  vectorStoreProviderMenuLabel,
} from "../../utils/vectorStoreSurfaceForm";
import { runVectorStoreCheck } from "../../services/readinessClient";
import { probeOutcomeDisplayItems } from "../../utils/probeOutcomeDisplay";
import RemoveDetailsButton from "./RemoveDetailsButton";

const F = INTAKE_FONT;
const PRIMARY = "#005BF0";

const fieldLabelSx = {
  display: "flex",
  alignItems: "center",
  gap: "1px",
  width: "100%",
  height: 24,
  m: 0,
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: "16px",
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#707070",
};

const requiredMarkSx = { color: "#DE350B" };

const fieldWrapperSx = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: "100%",
  minWidth: 0,
};

const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 40,
    minHeight: 40,
    boxSizing: "border-box",
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#000000",
    boxShadow: "inset 0px 2px 3px 0px rgba(85, 85, 85, 0.2)",
    "& fieldset": {
      borderColor: "#707070",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#707070",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#707070",
      borderWidth: "1px",
    },
    "& .MuiOutlinedInput-input": {
      height: 24,
      boxSizing: "content-box",
      padding: "8px 10px",
      fontFamily: "Lato, sans-serif",
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: 0,
      color: "#000000",
      "&::placeholder": {
        color: "#707070",
        opacity: 1,
      },
    },
  },
  "& .MuiSelect-select": {
    height: "24px !important",
    boxSizing: "content-box",
    display: "flex",
    alignItems: "center",
    padding: "8px 40px 8px 10px !important",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#000000",
  },
  "& .MuiSelect-icon": {
    position: "absolute",
    right: 10,
    top: "50%",
    width: 24,
    height: 24,
    transform: "translateY(-50%)",
  },
};

function FieldLabel({ children, required = false }) {
  return (
    <Typography sx={fieldLabelSx} component="label">
      {required ? <Box component="span" sx={requiredMarkSx}>*</Box> : null}
      {children}
    </Typography>
  );
}

function isVectorSuccess(report) {
  return report?.success === true || String(report?.overall_status || "").toLowerCase() === "ready";
}

/** A connection save is not a search. It has no probe notes or operation yet. */
function isUnprobedSave(report) {
  if (!report || String(report.overall_status || "").toLowerCase() !== "saved") return false;
  if (report.notes || report.operation_type) return false;
  if (report.success === true || report.success === false) return false;
  return true;
}

function sanitizeFieldValue(field, raw) {
  if (field.kind === "port") return sanitizePortInput(raw);
  if (field.kind === "digits") return String(raw ?? "").replace(/\D/g, "");
  return raw;
}

export default function Gate1VectorStorePanel({
  projectId,
  vectorForm,
  onVectorFormChange,
  vectorCredentials,
  onVectorCredentialsChange,
  vectorReport,
  staleVectorReport = null,
  onVectorReport,
  onSaveConnection,
  onRemoveDetails,
  saving = false,
  removing = false,
  saved = false,
  readOnly = false,
  errors = null,
}) {
  const [probeRunning, setProbeRunning] = useState(false);
  const [probeError, setProbeError] = useState("");
  const fieldErrors = errors || {};
  /* Mandatory stars/blocking are off for this card until asked to restore. */
  const sectionRequired = false;

  const providerOptions = VECTOR_STORE_PROVIDER_OPTIONS.filter((option) => !option.unavailable).map(
    (option) => option.value
  );
  const selectedProvider = providerOptions.includes(vectorForm.provider)
    ? vectorForm.provider
    : "";
  const providerFields = fieldsForProvider(selectedProvider);
  const isPineconeFetch = selectedProvider === "pinecone";
  const canTest = vectorStoreFormCanTest(vectorForm, vectorCredentials);
  const canSave = vectorStoreFormCanSave(vectorForm, vectorCredentials) && !readOnly && !saving;
  const canRemove =
    Boolean(selectedProvider) || Boolean(vectorReport) || Boolean(staleVectorReport) || saved;
  const currentReport = vectorReportMatchesForm(vectorReport, vectorForm)
    ? vectorReport
    : null;
  const previousReport =
    staleVectorReport && !vectorReportMatchesForm(staleVectorReport, vectorForm)
      ? staleVectorReport
      : null;

  const updateField = (key, value) => {
    setProbeError("");
    onVectorFormChange?.({ ...vectorForm, [key]: value });
  };

  const updateCredential = (key, value) => {
    onVectorCredentialsChange?.({
      ...vectorCredentials,
      [key]: value,
    });
  };

  const runProbe = async () => {
    const built = buildVectorStoreConfigPayload(vectorForm, vectorCredentials);
    if (built.error) {
      setProbeError(built.error);
      return;
    }

    try {
      setProbeRunning(true);
      setProbeError("");
      if (onSaveConnection) {
        try {
          await onSaveConnection();
        } catch {
          /* save must not skip the live vector-store check */
        }
      }
      const report = await runVectorStoreCheck(built.vector_store_config, { projectId });
      onVectorReport?.(report);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Vector store test failed.";
      setProbeError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setProbeRunning(false);
    }
  };

  const report = isUnprobedSave(currentReport) ? null : currentReport;
  const outcomeItems = probeOutcomeDisplayItems(report || {});
  const previousProvider = previousReport?.provider || "vector-store";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        border: "1px solid #E6E6E6",
        borderRadius: "8px",
        p: "24px",
        bgcolor: "#FFFFFF",
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          width: "100%",
          m: 0,
          fontFamily: "Lato, sans-serif",
          fontWeight: 900,
          fontStyle: "normal",
          fontSize: "20px",
          lineHeight: "100%",
          letterSpacing: 0,
          color: "#383838",
        }}
      >
        Vector store readiness
      </Typography>
      <Typography
        sx={{
          width: "100%",
          m: 0,
          fontFamily: "Lato, sans-serif",
          fontWeight: 400,
          fontStyle: "normal",
          fontSize: "14px",
          lineHeight: "140%",
          letterSpacing: 0,
          color: "#555555",
        }}
      >
        {isPineconeFetch
          ? "Optional. Fetch an existing Pinecone record by ID and verify that its metadata is accessible. You can continue without this check."
          : "Optional. Test similarity retrieval against the selected vector store. This is separate from SQL database connectivity, and you can continue without it."}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          columnGap: "24px",
          rowGap: "16px",
          width: "100%",
          minWidth: 0,
        }}
      >
        <Box sx={fieldWrapperSx}>
          <FieldLabel required={sectionRequired}>Provider</FieldLabel>
          <TextField
            select
            fullWidth
            size="small"
            value={selectedProvider}
            onChange={(e) => updateField("provider", e.target.value)}
            disabled={readOnly}
            error={Boolean(fieldErrors.provider)}
            helperText={fieldErrors.provider || ""}
            SelectProps={{
              displayEmpty: true,
              IconComponent: KeyboardArrowDownIcon,
              renderValue: (selected) => {
                const option = VECTOR_STORE_PROVIDER_OPTIONS.find((item) => item.value === selected);
                if (!option) {
                  return (
                    <Typography
                      sx={{
                        color: "#707070",
                        fontFamily: "Lato, sans-serif",
                        fontWeight: 400,
                        fontSize: "16px",
                        lineHeight: "24px",
                      }}
                    >
                      Select provider
                    </Typography>
                  );
                }
                return option.label;
              },
            }}
            sx={inputSx}
          >
            <MenuItem value="" disabled sx={{ display: "none" }}>
              Select provider
            </MenuItem>
            {VECTOR_STORE_PROVIDER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value} disabled={Boolean(option.unavailable)}>
                {vectorStoreProviderMenuLabel(option)}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {providerFields.map((field) => {
          const isCredential = Boolean(field.credential);
          const value = isCredential
            ? vectorCredentials[field.key] || ""
            : vectorForm[field.key] || "";
          return (
            <Box
              key={field.key}
              sx={{
                ...fieldWrapperSx,
                ...(field.fullWidth ? { gridColumn: { xs: "1", sm: "1 / -1" } } : {}),
              }}
            >
              <FieldLabel required={sectionRequired && field.required}>{field.label}</FieldLabel>
              <TextField
                fullWidth
                size="small"
                type={isCredential ? "password" : "text"}
                value={value}
                onChange={(e) => {
                  const next = sanitizeFieldValue(field, e.target.value);
                  if (isCredential) updateCredential(field.key, next);
                  else updateField(field.key, next);
                }}
                disabled={readOnly}
                placeholder={field.placeholder}
                autoComplete={isCredential ? "new-password" : "off"}
                error={Boolean(fieldErrors[field.key])}
                helperText={fieldErrors[field.key] || ""}
                sx={inputSx}
              />
            </Box>
          );
        })}
      </Box>

      {!readOnly && (
        <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={() => canSave && onSaveConnection?.()}
            disabled={!canSave}
            sx={{
              width: "fit-content",
              minWidth: 140,
              height: 32,
              boxSizing: "border-box",
              p: "4px 16px",
              textTransform: "none",
              fontWeight: 700,
              fontFamily: "Lato, sans-serif",
              fontSize: "13px",
              lineHeight: "24px",
              border: "1px solid #005BF0",
              color: PRIMARY,
              borderRadius: "4px",
              bgcolor: "#FFFFFF",
              "&.Mui-disabled": {
                border: "1px solid #E6E6E6",
                bgcolor: "#F5F5F5",
                color: "#BBBBBB",
              },
            }}
          >
            {saving ? "Saving…" : saved ? "Saved" : "Save connection"}
          </Button>
          <RemoveDetailsButton
            onClick={() => onRemoveDetails?.()}
            disabled={!canRemove || saving}
            removing={removing}
          />
          <Button
            variant="outlined"
            onClick={runProbe}
            disabled={!canTest || probeRunning}
            startIcon={probeRunning ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{
              width: "fit-content",
              minWidth: 179,
              height: 32,
              boxSizing: "border-box",
              p: "4px 16px",
              gap: "4px",
              textTransform: "none",
              fontWeight: 700,
              fontFamily: "Lato, sans-serif",
              fontSize: "13px",
              lineHeight: "24px",
              letterSpacing: 0,
              border: "1px solid #005BF0",
              color: PRIMARY,
              borderRadius: "4px",
              bgcolor: "#FFFFFF",
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.04)",
              "&:hover": {
                border: "1px solid #005BF0",
                bgcolor: "#FFFFFF",
                backgroundImage:
                  "linear-gradient(0deg, rgba(0, 91, 240, 0.08), rgba(0, 91, 240, 0.08))",
              },
              "&.Mui-disabled": {
                border: "1px solid #E6E6E6",
                bgcolor: "#F5F5F5",
                color: "#BBBBBB",
              },
            }}
          >
            {probeRunning
              ? "Testing…"
              : isPineconeFetch
                ? "Test Pinecone Retrieval"
                : "Test similarity search"}
          </Button>
        </Box>
      )}

      {previousReport && !currentReport && (
        <Alert severity="info" sx={{ mt: 1.5, fontSize: 13, fontFamily: F }}>
          Previous {previousProvider} result is stale and is not shown as the current provider
          result. Run a new similarity search for {selectedProvider || "the selected provider"}.
        </Alert>
      )}

      {probeError && (
        <Alert severity="warning" sx={{ mt: 1.5, fontSize: 13, fontFamily: F }}>
          {probeError}
        </Alert>
      )}

      {report && !probeError && (
        <Alert
          severity={isVectorSuccess(report) ? "success" : "error"}
          sx={{ mt: 1.5, fontSize: 13, fontFamily: F }}
        >
          {report.notes ||
            (isVectorSuccess(report)
              ? "Similarity search succeeded."
              : "Similarity search failed.")}
          {report.latencyMs != null ? ` (${report.latencyMs}ms)` : ""}
        </Alert>
      )}

      {report && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
          {report.provider ? (
            <Chip
              size="small"
              label={`Provider ${report.provider}`}
              sx={{ height: 22, fontFamily: F, bgcolor: "#F0F4F8", color: INTAKE_COLORS.g6 }}
            />
          ) : null}
          {outcomeItems.map((item) => (
            <Chip
              key={item.key}
              size="small"
              label={`${item.title} ${item.text}`}
              sx={{
                height: 22,
                fontWeight: 700,
                fontFamily: F,
                bgcolor: item.tone === "pass" ? "#E8FCF3" : "#FFD2C7",
                color: item.tone === "pass" ? "#00754E" : "#B82500",
              }}
            />
          ))}
          {report.response?.result_count != null && (
            <Chip
              size="small"
              label={`Results ${report.response.result_count}/${report.response.top_k ?? "—"}`}
              sx={{ height: 22, fontFamily: F, bgcolor: "#F0F4F8", color: INTAKE_COLORS.g6 }}
            />
          )}
        </Stack>
      )}

      {report?.operation_type === "describe_index" && (
        <Box
          sx={{
            mt: 0.5,
            p: 2,
            border: `1px solid ${INTAKE_COLORS.g2}`,
            borderRadius: "6px",
            bgcolor: "#FAFAFA",
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 900, fontFamily: F, mb: 1 }}>
            Pinecone Index
          </Typography>
          <Stack spacing={0.5}>
            <Typography sx={{ fontSize: 13, fontFamily: F }}>
              <strong>Index:</strong> {report.indexName || "—"}
            </Typography>
            <Typography sx={{ fontSize: 13, fontFamily: F }}>
              <strong>Namespace:</strong> {report.namespace || "(default)"}
            </Typography>
            <Typography sx={{ fontSize: 13, fontFamily: F }}>
              <strong>Vectors:</strong> {report.vectorCount ?? "—"}
            </Typography>
            {/* The index's own embedding size — execution must embed queries
                with a model that produces exactly this many dimensions. */}
            <Typography sx={{ fontSize: 13, fontFamily: F }}>
              <strong>Dimension:</strong> {report.dimension ?? "—"}
            </Typography>
            {report.metric ? (
              <Typography sx={{ fontSize: 13, fontFamily: F }}>
                <strong>Metric:</strong> {report.metric}
              </Typography>
            ) : null}
            <Typography sx={{ fontSize: 13, fontFamily: F }}>
              <strong>Latency:</strong> {report.latency_ms ?? report.latencyMs ?? 0} ms
            </Typography>
          </Stack>
        </Box>
      )}

      {report?.operation_type === "fetch_record" && report?.record && (
        <Box
          sx={{
            mt: 0.5,
            p: 2,
            border: `1px solid ${INTAKE_COLORS.g2}`,
            borderRadius: "6px",
            bgcolor: "#FAFAFA",
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 900, fontFamily: F, mb: 1 }}>
            Pinecone Retrieval
          </Typography>
          <Stack spacing={0.5}>
            <Typography sx={{ fontSize: 13, fontFamily: F }}>
              <strong>Index:</strong> {report.indexName || "—"}
            </Typography>
            <Typography sx={{ fontSize: 13, fontFamily: F }}>
              <strong>Namespace:</strong> {report.namespace || "(default)"}
            </Typography>
            <Typography sx={{ fontSize: 13, fontFamily: F }}>
              <strong>Record ID:</strong> {report.record.id}
            </Typography>
            <Typography sx={{ fontSize: 13, fontFamily: F }}>
              <strong>Latency:</strong> {report.latency_ms ?? report.latencyMs ?? 0} ms
            </Typography>
          </Stack>

          {report.warning ? (
            <Alert severity="warning" sx={{ mt: 1.5, fontSize: 13, fontFamily: F }}>
              {report.warning}
            </Alert>
          ) : null}

          {Object.keys(report.record.metadata || {}).length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: F, mb: 0.5 }}>
                Record Metadata
              </Typography>
              {Object.entries(report.record.metadata)
                .filter(([key]) => key !== "chunk_text")
                .map(([key, value]) => (
                  <Typography key={key} sx={{ fontSize: 12, fontFamily: F, overflowWrap: "anywhere" }}>
                    <strong>{key}:</strong>{" "}
                    {value != null && typeof value === "object"
                      ? JSON.stringify(value)
                      : String(value ?? "")}
                  </Typography>
                ))}
            </Box>
          )}

          {report.record.metadata?.chunk_text != null && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: F, mb: 0.5 }}>
                Chunk Text
              </Typography>
              <Typography sx={{ fontSize: 12, fontFamily: F, whiteSpace: "pre-wrap" }}>
                {String(report.record.metadata.chunk_text)}
              </Typography>
            </Box>
          )}

          {Array.isArray(report.record.values) && report.record.values.length > 0 && (
            <Typography sx={{ mt: 1.5, fontSize: 13, fontFamily: F }}>
              <strong>Vector:</strong> {report.record.values.length} dimensions
            </Typography>
          )}
          {!Array.isArray(report.record.values) && report.record.vector_dimension > 0 && (
            <Typography sx={{ mt: 1.5, fontSize: 13, fontFamily: F }}>
              <strong>Vector:</strong> {report.record.vector_dimension} dimensions
            </Typography>
          )}
        </Box>
      )}

      {Array.isArray(report?.results) && report.results.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: F, color: "#555", mb: 0.75 }}>
            Retrieved documents
          </Typography>
          <Stack spacing={0.75}>
            {report.results.slice(0, 5).map((row) => (
              <Box
                key={`${row.id}-${row.title}`}
                sx={{
                  p: 1,
                  borderRadius: "6px",
                  border: `1px solid ${INTAKE_COLORS.g2}`,
                  bgcolor: "#FAFAFA",
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: F }}>
                  {row.title || `id ${row.id}`}
                  {row.score != null
                    ? ` · score ${row.score}`
                    : row.distance != null
                      ? ` · distance ${row.distance}`
                      : ""}
                </Typography>
                {row.content_preview ? (
                  <Typography sx={{ fontSize: 12, fontFamily: F, color: "#555", mt: 0.25 }}>
                    {row.content_preview}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
